/**
 * API client tipado para ConexEd v2.
 *
 * Habla directamente con el backend en *.workers.dev. Cookies httpOnly
 * cross-site (SameSite=None) — siempre `credentials: 'include'`.
 * Fallback X-Session-ID via sessionStorage para casos donde el browser
 * bloquee third-party cookies (Safari ITP, Privacy Sandbox).
 */

import type {
    ApiError, User, Student, Liceo, Invitation, InvitePreview, SessionResponse,
    Company, Course, Professor, Tutor, Plan, LiceoWithUsage, ErrorLog,
    PortfolioEntry, BitacoraEntry, Observation, Notification,
    Evaluation, EvaluationTemplate,
    AttendanceRecord, AttendanceStatus, AttendanceSummary,
} from './types';

const API_BASE = (() => {
    if (typeof window === 'undefined') return '';
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
        return 'https://giti-backend.daguilera.workers.dev';
    }
    // En staging usaremos otro env eventualmente; por ahora todo apunta a prod.
    return 'https://giti-backend.daguilera.workers.dev';
})();

const SESSION_STORAGE_KEY = 'giti_session_id';
const CSRF_STORAGE_KEY = 'giti_csrf_token';

export class SessionError extends Error {
    constructor(public endpoint: string) {
        super('Session expired or invalid');
        this.name = 'SessionError';
    }
}

export class ApiException extends Error {
    constructor(public status: number, public body: ApiError, public endpoint: string) {
        super(body.error || `HTTP ${status}`);
        this.name = 'ApiException';
    }
}

function getSessionId(): string | null {
    try { return sessionStorage.getItem(SESSION_STORAGE_KEY); } catch { return null; }
}

export function setSessionId(id: string | null) {
    try {
        if (id) sessionStorage.setItem(SESSION_STORAGE_KEY, id);
        else sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } catch { /* private browsing */ }
}

function getCsrfToken(): string | null {
    try { return localStorage.getItem(CSRF_STORAGE_KEY); } catch { return null; }
}

function setCsrfToken(token: string | null) {
    try {
        if (token) localStorage.setItem(CSRF_STORAGE_KEY, token);
        else localStorage.removeItem(CSRF_STORAGE_KEY);
    } catch { /* */ }
}

interface FetchOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: unknown;
    skipAuth?: boolean;
}

async function apiFetch<T = unknown>(endpoint: string, opts: FetchOptions = {}): Promise<T> {
    const method = opts.method || 'GET';
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };

    const sessionId = getSessionId();
    if (sessionId && !opts.skipAuth) {
        headers['X-Session-ID'] = sessionId;
    }
    if (['POST', 'PUT', 'DELETE'].includes(method)) {
        const csrf = getCsrfToken();
        if (csrf) headers['X-CSRF-Token'] = csrf;
    }

    const url = `${API_BASE}${endpoint}`;

    let response: Response;
    try {
        response = await fetch(url, {
            method,
            headers,
            credentials: 'include',
            body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        });
    } catch (e) {
        throw new Error('Network error: ' + (e as Error).message);
    }

    if (!response.ok) {
        const body: ApiError = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        if (response.status === 401 && !opts.skipAuth) {
            throw new SessionError(endpoint);
        }
        throw new ApiException(response.status, body, endpoint);
    }

    return response.json() as Promise<T>;
}

// ─── Auth ───────────────────────────────────────────────────────────────────

export const authApi = {
    async login(email: string, password: string): Promise<SessionResponse> {
        const res = await apiFetch<SessionResponse>('/api/auth/login', {
            method: 'POST',
            body: { email, password },
            skipAuth: true,
        });
        if (res.sessionId) setSessionId(res.sessionId);
        if (res.csrfToken) setCsrfToken(res.csrfToken);
        return res;
    },

    async logout(): Promise<void> {
        await apiFetch('/api/auth/logout', { method: 'POST' }).catch(() => { /* always clean */ });
        setSessionId(null);
        setCsrfToken(null);
    },

    async getCurrentUser(): Promise<User> {
        const res = await apiFetch<{ user: User; csrfToken?: string }>('/api/auth/current');
        if (res.csrfToken) setCsrfToken(res.csrfToken);
        return res.user;
    },

    async registerIndividual(data: {
        name: string;
        email: string;
        password: string;
        liceo_name?: string;
    }): Promise<SessionResponse> {
        const res = await apiFetch<SessionResponse>('/api/auth/register-individual', {
            method: 'POST',
            body: data,
            skipAuth: true,
        });
        if (res.sessionId) setSessionId(res.sessionId);
        if (res.csrfToken) setCsrfToken(res.csrfToken);
        return res;
    },

    async registerWithInvite(data: {
        token: string;
        name: string;
        password: string;
    }): Promise<SessionResponse> {
        const res = await apiFetch<SessionResponse>('/api/auth/register-with-invite', {
            method: 'POST',
            body: data,
            skipAuth: true,
        });
        if (res.sessionId) setSessionId(res.sessionId);
        if (res.csrfToken) setCsrfToken(res.csrfToken);
        return res;
    },

    async google(credential: string, opts: { invite_token?: string; register_individual?: boolean } = {}): Promise<SessionResponse> {
        const res = await apiFetch<SessionResponse>('/api/auth/google', {
            method: 'POST',
            body: { credential, ...opts },
            skipAuth: true,
        });
        if (res.sessionId) setSessionId(res.sessionId);
        if (res.csrfToken) setCsrfToken(res.csrfToken);
        return res;
    },

    async changePassword(current_password: string, new_password: string): Promise<void> {
        await apiFetch('/api/auth/change-password', {
            method: 'POST',
            body: { current_password, new_password },
        });
    },

    // Backend responde 200 siempre (no revela si el email existe).
    async forgotPassword(email: string): Promise<void> {
        await apiFetch('/api/auth/forgot-password', {
            method: 'POST',
            body: { email },
            skipAuth: true,
        });
    },

    async resetPassword(token: string, new_password: string): Promise<void> {
        await apiFetch('/api/auth/reset-password', {
            method: 'POST',
            body: { token, new_password },
            skipAuth: true,
        });
    },
};

// ─── Students ───────────────────────────────────────────────────────────────

export const studentsApi = {
    async list(): Promise<Student[]> {
        const res = await apiFetch<{ students: Student[] }>('/api/students');
        return res.students;
    },

    async getById(id: number): Promise<Student> {
        const res = await apiFetch<{ student: Student }>(`/api/students/${id}`);
        return res.student;
    },

    async getMe(): Promise<Student | null> {
        try {
            const res = await apiFetch<{ student: Student }>('/api/students/me');
            return res.student;
        } catch (e) {
            if (e instanceof ApiException && e.status === 404) return null;
            throw e;
        }
    },
    async getContact(studentId: number): Promise<{
        student: { id: number; name: string; email: string | null; photo: string | null; specialty: string | null; company: string | null };
        guardian: { name: string | null; relationship: string | null; email: string | null; phone: string | null; address: string | null } | null;
        professor: { id: number; name: string; email: string; photo: string | null; liceo: string | null } | null;
    }> {
        return apiFetch(`/api/students/${studentId}/contact`);
    },
};

export const studentsCrudApi = {
    async update(id: number, data: Partial<Student>): Promise<void> {
        await apiFetch(`/api/students/${id}`, { method: 'PUT', body: data });
    },
    async create(data: Partial<Student>): Promise<{ studentId: number }> {
        return apiFetch('/api/students', { method: 'POST', body: data });
    },
    async remove(id: number): Promise<void> {
        await apiFetch(`/api/students/${id}`, { method: 'DELETE' });
    },
};

// ─── Liceos / Super Admin ──────────────────────────────────────────────────

export const liceosApi = {
    async list(): Promise<Liceo[]> {
        const res = await apiFetch<{ liceos: Liceo[] }>('/api/super-admin/liceos');
        return res.liceos;
    },
};

export const superAdminApi = {
    async getStats(): Promise<{ liceos: number; users: { role: string; count: number }[]; students: { status: string; count: number }[]; companies: number }> {
        return apiFetch('/api/super-admin/stats');
    },
    async getLiceosWithUsage(): Promise<LiceoWithUsage[]> {
        const res = await apiFetch<{ liceos: LiceoWithUsage[] }>('/api/super-admin/liceos');
        return res.liceos;
    },
    async getPlans(): Promise<Plan[]> {
        const res = await apiFetch<{ plans: Plan[] }>('/api/super-admin/plans');
        return res.plans;
    },
    async createLiceo(data: { nombre: string; direccion?: string; telefono?: string; email?: string }): Promise<{ id: number }> {
        return apiFetch('/api/super-admin/liceos', { method: 'POST', body: data });
    },
    async updateLiceo(id: number, data: Partial<Liceo>): Promise<void> {
        await apiFetch(`/api/super-admin/liceos/${id}`, { method: 'PUT', body: data });
    },
    async deleteLiceo(id: number): Promise<void> {
        await apiFetch(`/api/super-admin/liceos/${id}`, { method: 'DELETE' });
    },
    async updateLiceoPlan(liceoId: number, planId: number): Promise<void> {
        await apiFetch(`/api/super-admin/liceos/${liceoId}/plan`, {
            method: 'PUT',
            body: { plan_id: planId },
        });
    },
    async getUsers(): Promise<User[]> {
        const res = await apiFetch<{ users: User[] }>('/api/super-admin/users');
        return res.users;
    },
    async deleteUser(id: number): Promise<void> {
        await apiFetch(`/api/super-admin/users/${id}`, { method: 'DELETE' });
    },
};

// ─── Errores ───────────────────────────────────────────────────────────────

export const errorsApi = {
    async stats(): Promise<{ last_24h: number; last_7d: number; unresolved: number; total: number }> {
        return apiFetch('/api/errors/stats');
    },
    async list(opts: { page?: number; limit?: number; resolved?: '0' | '1' | '' } = {}): Promise<{
        errors: ErrorLog[]; total: number; page: number; limit: number;
    }> {
        const qs = new URLSearchParams({ page: String(opts.page ?? 1), limit: String(opts.limit ?? 50) });
        if (opts.resolved !== undefined && opts.resolved !== '') qs.set('resolved', opts.resolved);
        return apiFetch(`/api/errors?${qs.toString()}`);
    },
    async resolve(id: number): Promise<void> {
        await apiFetch(`/api/errors/${id}/resolve`, { method: 'PUT' });
    },
    async remove(id: number): Promise<void> {
        await apiFetch(`/api/errors/${id}`, { method: 'DELETE' });
    },
};

// ─── Companies / Courses / Professors / Tutors ─────────────────────────────

export const companiesApi = {
    async list(): Promise<Company[]> {
        const res = await apiFetch<{ companies: Company[] }>('/api/companies');
        return res.companies;
    },
    async create(data: Partial<Company>): Promise<{ companyId: number }> {
        return apiFetch('/api/companies', { method: 'POST', body: data });
    },
    async update(id: number, data: Partial<Company>): Promise<void> {
        await apiFetch(`/api/companies/${id}`, { method: 'PUT', body: data });
    },
    async remove(id: number): Promise<void> {
        await apiFetch(`/api/companies/${id}`, { method: 'DELETE' });
    },
};

export const coursesApi = {
    async list(): Promise<Course[]> {
        const res = await apiFetch<{ courses: Course[] }>('/api/courses');
        return res.courses;
    },
    async create(data: { name: string; specialty?: string; professorId?: number }): Promise<{ courseId: number }> {
        return apiFetch('/api/courses', { method: 'POST', body: data });
    },
    async update(id: number, data: Partial<Course> & { professorId?: number }): Promise<void> {
        await apiFetch(`/api/courses/${id}`, { method: 'PUT', body: data });
    },
    async remove(id: number): Promise<void> {
        await apiFetch(`/api/courses/${id}`, { method: 'DELETE' });
    },
};

export const professorsApi = {
    async list(): Promise<Professor[]> {
        const res = await apiFetch<{ professors: Professor[] }>('/api/professors');
        return res.professors;
    },
    async create(data: Partial<Professor>): Promise<{ professor: Professor }> {
        return apiFetch('/api/professors', { method: 'POST', body: data });
    },
    async update(id: number, data: Partial<Professor>): Promise<void> {
        await apiFetch(`/api/professors/${id}`, { method: 'PUT', body: data });
    },
};

export const tutorsApi = {
    async list(): Promise<Tutor[]> {
        const res = await apiFetch<{ tutors: Tutor[] }>('/api/tutors');
        return res.tutors;
    },
    async create(data: Partial<Tutor>): Promise<{ tutor: Tutor }> {
        return apiFetch('/api/tutors', { method: 'POST', body: data });
    },
};

// ─── Users (admin) ─────────────────────────────────────────────────────────

export const usersApi = {
    async list(): Promise<User[]> {
        const res = await apiFetch<{ users: User[] }>('/api/users');
        return res.users;
    },
    async getById(id: number): Promise<User> {
        const res = await apiFetch<{ user: User }>(`/api/users/${id}`);
        return res.user;
    },
    async update(id: number, data: Partial<User>): Promise<void> {
        await apiFetch(`/api/users/${id}`, { method: 'PUT', body: data });
    },
    async create(data: Partial<User> & { password: string }): Promise<{ userId: number }> {
        return apiFetch('/api/users', { method: 'POST', body: data });
    },
};

// ─── Portfolio / Bitacora / Observations ───────────────────────────────────

export const portfolioApi = {
    async listForStudent(studentId: number, opts: { page?: number; limit?: number } = {}): Promise<{ entries: PortfolioEntry[]; total: number }> {
        const qs = new URLSearchParams({ page: String(opts.page ?? 1), limit: String(opts.limit ?? 50) });
        return apiFetch(`/api/portfolio/student/${studentId}?${qs.toString()}`);
    },
    async create(data: Partial<PortfolioEntry>): Promise<{ entryId: number }> {
        return apiFetch('/api/portfolio', { method: 'POST', body: data });
    },
    async update(id: number, data: Partial<PortfolioEntry>): Promise<void> {
        await apiFetch(`/api/portfolio/${id}`, { method: 'PUT', body: data });
    },
    async remove(id: number): Promise<void> {
        await apiFetch(`/api/portfolio/${id}`, { method: 'DELETE' });
    },
};

export const bitacoraApi = {
    // Backend scopea automáticamente según rol:
    //   - estudiante: solo entries sobre sí mismo
    //   - profesor: solo students asignados
    //   - maestro_guia: solo students de su company (via tutors)
    //   - admin: todo el liceo
    async list(filters: { studentId?: number; maestroId?: number } = {}): Promise<BitacoraEntry[]> {
        const qs = new URLSearchParams();
        if (filters.studentId) qs.set('student_id', String(filters.studentId));
        if (filters.maestroId) qs.set('maestro_id', String(filters.maestroId));
        const path = qs.toString() ? `/api/bitacora?${qs.toString()}` : '/api/bitacora';
        const res = await apiFetch<{ entries: BitacoraEntry[] }>(path);
        return res.entries;
    },
    async create(data: Partial<BitacoraEntry>): Promise<{ entryId: number }> {
        return apiFetch('/api/bitacora', { method: 'POST', body: data });
    },
    async update(id: number, data: Partial<BitacoraEntry>): Promise<void> {
        await apiFetch(`/api/bitacora/${id}`, { method: 'PUT', body: data });
    },
    async delete(id: number): Promise<void> {
        await apiFetch(`/api/bitacora/${id}`, { method: 'DELETE' });
    },
};

export const observationsApi = {
    async listForStudent(studentId: number): Promise<Observation[]> {
        const res = await apiFetch<{ observations: Observation[] }>(`/api/observations/student/${studentId}`);
        return res.observations;
    },
    async create(data: Partial<Observation>): Promise<{ observationId: number }> {
        return apiFetch('/api/observations', { method: 'POST', body: data });
    },
};

// ─── Evaluations ────────────────────────────────────────────────────────────

export const evaluationsApi = {
    async listForStudent(studentId: number): Promise<Evaluation[]> {
        const res = await apiFetch<{ evaluations: Evaluation[] }>(`/api/evaluations/student/${studentId}`);
        return (res.evaluations ?? []).map(e => ({
            ...e,
            scores: typeof e.scores === 'string' ? JSON.parse(e.scores || '[]') : e.scores,
        }));
    },
    async create(data: {
        student_id: number;
        evaluation_type: 'inicial' | 'intermedia' | 'final';
        template_id?: string;
        scores?: Array<{ dimensionId: string; score: number }>;
        signature?: string;
    }): Promise<{ evaluationId: number }> {
        return apiFetch('/api/evaluations', { method: 'POST', body: data });
    },
    async update(id: number, data: { scores?: Array<{ dimensionId: string; score: number }>; signature?: string }): Promise<void> {
        await apiFetch(`/api/evaluations/${id}`, { method: 'PUT', body: data });
    },
    async delete(id: number): Promise<void> {
        await apiFetch(`/api/evaluations/${id}`, { method: 'DELETE' });
    },
};

// ─── Attendance ─────────────────────────────────────────────────────────────

export const attendanceApi = {
    async listForStudent(studentId: number, range?: { from?: string; to?: string }): Promise<AttendanceRecord[]> {
        const qs = new URLSearchParams();
        if (range?.from) qs.set('from', range.from);
        if (range?.to) qs.set('to', range.to);
        const path = `/api/attendance/student/${studentId}${qs.toString() ? `?${qs.toString()}` : ''}`;
        const res = await apiFetch<{ attendance: AttendanceRecord[] }>(path);
        return res.attendance;
    },
    async summary(studentId: number): Promise<AttendanceSummary> {
        return apiFetch<AttendanceSummary>(`/api/attendance/summary/${studentId}`);
    },
    async saveBulk(studentId: number, records: Array<{ date: string; status: AttendanceStatus }>): Promise<{ upserted: number }> {
        return apiFetch('/api/attendance/bulk', {
            method: 'POST',
            body: { student_id: studentId, records },
        });
    },
};

export const evaluationTemplatesApi = {
    async list(): Promise<EvaluationTemplate[]> {
        // Backend devuelve raw array (verificado en bug similar de notifications)
        const res = await apiFetch<EvaluationTemplate[] | { templates: EvaluationTemplate[] }>('/api/evaluation-templates');
        const arr = Array.isArray(res) ? res : (res.templates ?? []);
        return arr.map(t => ({
            ...t,
            dimensions: typeof t.dimensions === 'string' ? JSON.parse(t.dimensions) : t.dimensions,
        }));
    },
    async get(id: string): Promise<EvaluationTemplate> {
        const t = await apiFetch<EvaluationTemplate>(`/api/evaluation-templates/${id}`);
        return {
            ...t,
            dimensions: typeof t.dimensions === 'string' ? JSON.parse(t.dimensions as unknown as string) : t.dimensions,
        };
    },
    async create(data: { name: string; description?: string; dimensions: Array<{ id: string; name: string; description?: string; weight?: number }> }): Promise<EvaluationTemplate> {
        return apiFetch('/api/evaluation-templates', { method: 'POST', body: data });
    },
    async update(id: string, data: { name: string; description?: string; dimensions: Array<{ id: string; name: string; description?: string; weight?: number }> }): Promise<void> {
        await apiFetch(`/api/evaluation-templates/${id}`, { method: 'PUT', body: data });
    },
    async remove(id: string): Promise<void> {
        await apiFetch(`/api/evaluation-templates/${id}`, { method: 'DELETE' });
    },
};

// ─── Achievements ──────────────────────────────────────────────────────────

export interface AchievementDef {
    id: number;
    key: string;
    emoji: string | null;
    title: string;
    description: string | null;
    sort_order: number;
    liceo_id: number;
}

export const achievementsApi = {
    async list(): Promise<AchievementDef[]> {
        const res = await apiFetch<{ achievements: AchievementDef[] }>('/api/achievements');
        return res.achievements;
    },
    async create(data: { key: string; title: string; emoji?: string; description?: string; sort_order?: number }): Promise<{ id: number }> {
        return apiFetch('/api/achievements', { method: 'POST', body: data });
    },
    async update(id: number, data: Partial<Omit<AchievementDef, 'id' | 'liceo_id'>>): Promise<void> {
        await apiFetch(`/api/achievements/${id}`, { method: 'PUT', body: data });
    },
    async remove(id: number): Promise<void> {
        await apiFetch(`/api/achievements/${id}`, { method: 'DELETE' });
    },
    async assignToStudent(defId: number, studentId: number): Promise<{ action: 'assigned' | 'removed' }> {
        return apiFetch(`/api/achievements/${defId}/assign/${studentId}`, { method: 'POST' });
    },
    async toggleMine(defId: number): Promise<{ action: 'assigned' | 'removed'; achievements: string[] }> {
        return apiFetch(`/api/achievements/me/toggle/${defId}`, { method: 'POST' });
    },
};

// ─── Notifications ─────────────────────────────────────────────────────────

export const notificationsApi = {
    async list(): Promise<Notification[]> {
        // Backend returns raw array, not wrapped object
        return apiFetch<Notification[]>('/api/notifications');
    },
    async unreadCount(): Promise<number> {
        const res = await apiFetch<{ count: number }>('/api/notifications/unread-count');
        return res.count;
    },
    async markRead(id: number): Promise<void> {
        await apiFetch(`/api/notifications/${id}/read`, { method: 'PUT' });
    },
    async markAllRead(): Promise<void> {
        await apiFetch('/api/notifications/read-all', { method: 'PUT' });
    },
};

// ─── Invitations ────────────────────────────────────────────────────────────

export const invitationsApi = {
    async list(): Promise<Invitation[]> {
        const res = await apiFetch<{ invitations: Invitation[] }>('/api/invitations');
        return res.invitations;
    },

    async preview(token: string): Promise<InvitePreview> {
        try {
            return await apiFetch<InvitePreview>(`/api/invitations/${token}`, { skipAuth: true });
        } catch (e) {
            if (e instanceof ApiException) return { valid: false, ...(e.body as object) };
            throw e;
        }
    },

    async create(data: {
        email: string;
        role: 'estudiante' | 'profesor' | 'maestro_guia' | 'administrador';
        expires_in_days?: number;
    }): Promise<{ id: number; token: string; invite_url: string }> {
        return apiFetch('/api/invitations', { method: 'POST', body: data });
    },

    async revoke(id: number): Promise<void> {
        await apiFetch(`/api/invitations/${id}`, { method: 'DELETE' });
    },
};

// ─── MFA (TOTP) — admin / super_admin ──────────────────────────────────────

export const mfaApi = {
    async setup(): Promise<{ secret: string; uri: string }> {
        return apiFetch('/api/mfa/setup', { method: 'POST' });
    },
    async verifySetup(code: string): Promise<void> {
        await apiFetch('/api/mfa/verify-setup', { method: 'POST', body: { code } });
    },
    async disable(code: string): Promise<void> {
        await apiFetch('/api/mfa/disable', { method: 'POST', body: { code } });
    },
};

// ─── Analytics ─────────────────────────────────────────────────────────────

export const analyticsApi = {
    async live(): Promise<{
        timestamp: string;
        real_time_metrics: { total_requests_today: string; active_sessions: number; unique_users_24h: number };
        usage_by_liceo: Array<{ liceo_id: number; liceo_name: string; session_count_today: number }>;
    }> {
        return apiFetch('/api/analytics/live');
    },
    async daily(): Promise<{
        date: string;
        sessions_today: number;
        unique_users_today: number;
        top_active_users: Array<{ name: string; role: string; login_count: number }>;
    }> {
        return apiFetch('/api/analytics/daily');
    },
    async evaluationTrends(): Promise<{
        trends: Array<{ month: string; type: string; avg: number; count: number }>;
    }> {
        return apiFetch('/api/analytics/evaluation-trends');
    },
};

// ─── Skills (catálogo MINEDUC global, read-only) ───────────────────────────

export interface SkillsCatalog {
    OAGs: string[];
    especialidades: Record<string, string[]>;
}

export const skillsApi = {
    async getCatalog(): Promise<SkillsCatalog> {
        const res = await apiFetch<{ skills: SkillsCatalog }>('/api/skills');
        return res.skills;
    },
    async getBySpecialty(name: string): Promise<string[]> {
        const res = await apiFetch<{ skills: string[] }>(`/api/skills/specialty/${encodeURIComponent(name)}`);
        return res.skills;
    },
};

// ─── Files (R2) ────────────────────────────────────────────────────────────

export const filesApi = {
    /**
     * Sube un file via multipart/form-data al endpoint backend, que valida
     * magic bytes + tamaño y guarda en R2 con la key {liceo_id}/{category}/...
     * Backend retorna `url` con path interno; convertimos a URL absoluta acá
     * para que el browser pueda incrustarlo (el GET requiere session).
     */
    async upload(file: File, category: 'portfolio' | 'document' | 'evaluation' | 'avatar' = 'general' as 'portfolio' | 'document' | 'evaluation' | 'avatar'): Promise<{
        key: string; url: string; filename: string; size: number; type: string;
    }> {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('category', category);

        const headers: Record<string, string> = {};
        const sessionId = getSessionId();
        if (sessionId) headers['X-Session-ID'] = sessionId;
        const csrf = getCsrfToken();
        if (csrf) headers['X-CSRF-Token'] = csrf;

        const res = await fetch(`${API_BASE}/api/files/upload`, {
            method: 'POST',
            credentials: 'include',
            headers,
            body: fd,
        });
        if (!res.ok) {
            const body: ApiError = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
            throw new ApiException(res.status, body, '/api/files/upload');
        }
        const json = await res.json() as { key: string; url: string; filename: string; size: number; type: string };
        return {
            ...json,
            url: `${API_BASE}${json.url}`, // absolutize so <img src> works cross-origin
        };
    },

    async remove(key: string): Promise<void> {
        await apiFetch(`/api/files/${key}`, { method: 'DELETE' });
    },
};

// ─── Health (server-side healthcheck, no relación con student health) ─────

export const healthApi = {
    async check(): Promise<{ status: string; db: string; db_ms: number }> {
        return apiFetch('/api/health', { skipAuth: true });
    },
};

// ─── Student PII (guardian + health) ───────────────────────────────────────

export interface GuardianInfo {
    name: string | null;
    relationship: string | null;
    email: string | null;
    phone: string | null;
    address: string | null;
}

export interface StudentHealthInfo {
    allergies: string | null;
    medications: string | null;
    pre_occupational_exam: string | null;
    blood_type: string | null;
    emergency_contact_name: string | null;
    emergency_contact_phone: string | null;
    medical_notes: string | null;
    documents?: Array<{
        id: number;
        document_name: string;
        document_url: string;
        document_type: string;
        uploaded_at: string;
    }>;
}

export const studentPiiApi = {
    async getGuardian(studentId: number): Promise<GuardianInfo> {
        const res = await apiFetch<{ guardian: GuardianInfo }>(`/api/students/${studentId}/guardian`);
        return res.guardian;
    },
    async updateGuardian(studentId: number, data: Partial<GuardianInfo>): Promise<void> {
        await apiFetch(`/api/students/${studentId}/guardian`, { method: 'PUT', body: data });
    },
    // Si no hay consent, backend devuelve { consent_required: true } con 200.
    async getHealth(studentId: number): Promise<{ health: StudentHealthInfo } | { consent_required: true; consentType: string }> {
        return apiFetch(`/api/students/${studentId}/health`);
    },
    async updateHealth(studentId: number, data: Partial<StudentHealthInfo>): Promise<{ success: true } | { consent_required: true; consentType: string }> {
        return apiFetch(`/api/students/${studentId}/health`, { method: 'PUT', body: data });
    },
};

// ─── Privacy / consents (Ley 21.719) ───────────────────────────────────────

export interface Consent {
    id: number;
    user_id: number;
    student_id: number | null;
    consent_type: string;
    granted_by: string;
    granted_by_rut: string | null;
    granted_by_relationship: string | null;
    granted_at: string;
    revoked_at: string | null;
    liceo_id: number | null;
}

export const privacyApi = {
    async grant(data: {
        studentId?: number;
        consentType: 'data_processing' | 'health_data' | 'photo_release' | 'parental';
        grantedBy: string;
        grantedByRut?: string;
        grantedByRelationship?: string;
    }): Promise<{ id: number }> {
        return apiFetch('/api/privacy/consent', { method: 'POST', body: data });
    },
    async list(studentId: number): Promise<Consent[]> {
        const res = await apiFetch<{ consents: Consent[] }>(`/api/privacy/consent/${studentId}`);
        return res.consents;
    },
    async revoke(id: number): Promise<void> {
        await apiFetch(`/api/privacy/consent/${id}/revoke`, { method: 'POST' });
    },
    async export(studentId: number): Promise<unknown> {
        return apiFetch(`/api/privacy/export/${studentId}`);
    },
};
